import { Link } from 'react-router-dom'
import {
  Anchor,
  Check,
  Clock,
  Loader2,
  ShieldCheck,
  ShoppingBasket,
  Sparkles,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'
import { formatTime } from '@/lib/utils'
import type { Recipe } from '@/types/recipe'
import type { WeekMenuItem } from '@/types/menu'

interface WeekMenuCardProps {
  item: WeekMenuItem
  recipe?: Recipe
  inBasket: boolean
  onToggleBasket: () => void
  isExpanding?: boolean
  onSaveIdea?: () => void
  isSaving?: boolean
}

const BasketToggle = ({
  inBasket,
  isBusy,
  onToggle,
}: {
  inBasket: boolean
  isBusy: boolean
  onToggle: () => void
}) => (
  <Button
    variant={inBasket ? 'outline' : 'primary'}
    size="sm"
    className="flex-1"
    onClick={onToggle}
    disabled={isBusy}
  >
    {isBusy ? (
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    ) : inBasket ? (
      <Check className="mr-2 h-4 w-4" />
    ) : (
      <ShoppingBasket className="mr-2 h-4 w-4" />
    )}
    {isBusy ? 'Recept uitwerken...' : inBasket ? 'In mandje' : 'Voeg toe aan mandje'}
  </Button>
)

export function WeekMenuCard({
  item,
  recipe,
  inBasket,
  onToggleBasket,
  isExpanding = false,
  onSaveIdea,
  isSaving = false,
}: WeekMenuCardProps) {
  const isIdea = item.kind === 'idea'
  const idea = isIdea ? item.idea : undefined
  const expandedRecipe = isIdea ? item.recipe : undefined
  const savedRecipeId = isIdea ? item.savedRecipeId : undefined

  const title = recipe?.title ?? expandedRecipe?.title ?? idea?.title ?? ''
  const description = recipe?.description ?? expandedRecipe?.description ?? idea?.description
  const imageUrl = recipe?.image_url ?? expandedRecipe?.image_url
  const totalTime =
    ((recipe?.prep_time ?? expandedRecipe?.prep_time ?? idea?.prep_time) || 0) +
    ((recipe?.cook_time ?? expandedRecipe?.cook_time ?? idea?.cook_time) || 0)
  const servings = recipe?.servings ?? expandedRecipe?.servings ?? idea?.servings
  const difficulty = recipe?.difficulty ?? expandedRecipe?.difficulty ?? idea?.difficulty
  const tags = recipe?.tags ?? expandedRecipe?.tags ?? idea?.tags ?? []
  const reason = item.kind === 'collection' ? item.reason : idea?.why_it_fits
  const anchorTitle = idea?.anchor_title
  const detailId = item.kind === 'collection' ? item.recipeId : savedRecipeId

  return (
    <Card className="flex h-full flex-col overflow-hidden transition-all duration-200 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600">
      <div className="relative aspect-video overflow-hidden">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-warm-100 to-warm-200">
            <span className="text-5xl opacity-60">{isIdea ? '✨' : '🍽️'}</span>
          </div>
        )}

        <div className="absolute left-2 top-2">
          {isIdea ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs font-medium text-orange-700 shadow-sm backdrop-blur">
              <Sparkles className="h-3 w-3" />
              Nieuw voorstel
            </span>
          ) : recipe?.is_curated ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs font-medium text-green-700 shadow-sm backdrop-blur">
              <ShieldCheck className="h-3 w-3" />
              Gecontroleerd
            </span>
          ) : null}
        </div>

        {difficulty ? (
          <div className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 shadow-sm backdrop-blur">
            <DifficultyIndicator difficulty={difficulty} showLabel={false} />
          </div>
        ) : null}
      </div>

      <CardContent className="flex flex-1 flex-col gap-4 p-5">
        <div className="space-y-2">
          <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-gray-900 dark:text-slate-50">
            {title}
          </h3>

          {description && (
            <p className="line-clamp-2 text-sm leading-relaxed text-gray-600 dark:text-slate-200">
              {description}
            </p>
          )}

          {isIdea && anchorTitle && (
            <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-slate-400">
              <Anchor className="mt-0.5 h-3 w-3 flex-shrink-0" />
              <span>
                Gebaseerd op <span className="font-medium">{anchorTitle}</span>
              </span>
            </p>
          )}

          {reason && (
            <p className="line-clamp-3 rounded-lg bg-orange-50 px-3 py-2 text-xs leading-relaxed text-orange-900 dark:bg-orange-950/60 dark:text-orange-100">
              {reason}
            </p>
          )}
        </div>

        {/* Pinned to the bottom so meta, tags and actions line up across cards
            regardless of how long the title and description run. */}
        <div className="mt-auto space-y-3">
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-slate-300">
            {totalTime > 0 && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <span>{formatTime(totalTime)}</span>
              </div>
            )}
            {servings ? (
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                <span>{servings}</span>
              </div>
            ) : null}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800 dark:bg-orange-950 dark:text-orange-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-slate-800">
            <BasketToggle inBasket={inBasket} isBusy={isExpanding} onToggle={onToggleBasket} />

            {isIdea && expandedRecipe && !savedRecipeId && (
              <Button variant="outline" size="sm" onClick={onSaveIdea} disabled={isSaving}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Bewaar
              </Button>
            )}

            {detailId && (
              <Button asChild variant="ghost" size="sm">
                <Link to={`/recepten/${detailId}`}>Bekijk</Link>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
